import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import { Facebook, Instagram, Linkedin, Mail, MessageCircle, Phone } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BackToTopButton from "../components/BackToTopButton";
import FloatingSocialButtons from "../components/FloatingSocialButtons";
import Newsletter from "../components/Newsletter";

interface TeamMember {
  name: string;
  role: string;
  photo: string;
  bio: string;
  social: {
    facebook?: string;
    linkedin?: string;
    email?: string;
    whatsapp?: string;
  };
}

const AboutPage = () => {
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [dbMembers, setDbMembers] = useState<TeamMember[]>([]);
  const hasAnimatedTeam = useRef(false);
  
  useEffect(() => {
    document.title = "About Us - DG IT Pvt. Ltd.";
    window.scrollTo(0, 0);
  }, []);

  // Fetch DB team members
  useEffect(() => {
    const fetchDbMembers = async () => {
      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      
      if (!error && data) {
        const mapped: TeamMember[] = data.map((m) => {
          const socialLinks = (m.social_links as Record<string, string>) || {};
          return {
            name: m.name,
            role: m.role,
            photo: m.image_url || '/uploads/logo.png',
            bio: m.description || '',
            social: {
              email: socialLinks.email,
              whatsapp: socialLinks.phone,
            },
          };
        });
        setDbMembers(mapped);
      }
    };
    fetchDbMembers();
  }, []);

  // Mark that initial team animation has run after first mount
  useEffect(() => {
    hasAnimatedTeam.current = true;
  }, []);

  const closeModal = () => {
    setSelectedMember(null);
  };

  const teamMembers: TeamMember[] = [
    { name: "Gokul Dhami", role: "Founder & CEO", photo: "/uploads/gokul.jpg", 
      bio: "Gokul Dhami – CEO & CMO Founder and driving force behind DG IT, Gokul Dhami leads with innovation and expertise in digital marketing and business strategy. With years of experience in IT, freelancing, and content creation, he is dedicated to helping businesses establish a strong digital presence. His vision and leadership continue to push DG IT towards new heights in the digital landscape.",
      social: { facebook: "https://www.facebook.com/DGITAgency", linkedin: "https://np.linkedin.com/company/dg-it", email: "dhamigokulsingh70@gmail.com", whatsapp: "9742846801" }
    },
    { name: "Aruna Mahara", role: "Co-founder & Content Creator", photo: "/uploads/Aruna.jpg",
      bio: "Aruna Mahara – Co-founder & Content Creator As the Chief Operating Officer of DG IT, Aruna Mahara ensures smooth operations and strategic growth. With a strong background in content creation, she plays a key role in crafting engaging and impactful digital content. Her expertise in storytelling and brand messaging helps businesses connect with their audiences effectively.",
      social: { facebook: "https://www.facebook.com/DGITAgency", linkedin: "https://np.linkedin.com/company/dg-it", email: "aruna@dgit.com.np", whatsapp: "9742846801" }
    },
    { name: "Amar Dhami", role: "Co-founder & Editor", photo: "/uploads/Amar.jpg",
      bio: "Amar Dhami – Co-founder & Editor As the Chief Business Development Officer at DG IT, Amar Dhami plays a crucial role in expanding business opportunities and forging strong client relationships. With his expertise in video editing and creative storytelling, he ensures high-quality content that meets industry standards. His strategic vision and editing skills contribute to the growth and success of DG IT.",
      social: { facebook: "https://www.facebook.com/DGITAgency", linkedin: "https://np.linkedin.com/company/dg-it", email: "amar@dgit.com.np", whatsapp: "9742846801" }
    },
    { name: "Mukesh Nath", role: "Senior Web Development Instructor & IT Expert", photo: "/uploads/mukeshnath-new.jpg",
      bio: "Expert in full-stack development with focus on modern JavaScript frameworks with a passion for creating user-friendly and visually appealing websites. He is proficient in React, JavaScript, and other modern web technologies.",
      social: { facebook: "https://www.facebook.com/mukesh.nath.962844", linkedin: "https://www.linkedin.com/in/devlopermukesh", email: "developermukeshnath@gmail.com", whatsapp: "9809424862" }
    },
    { name: "Sunil Dhami", role: "Senior Editor & Editing Head", photo: "/uploads/sunil.jpg",
      bio: "Sunil Dhami – CFO & Video Editing Head DG IT,Sunil Dhami manages the company's all videos planning and stability. Alongside his editing expertise, he leads the video editing team, ensuring top-quality production and creative storytelling. His strong analytical skills and creative vision make him an essential part of DG IT's growth and success.",
      social: { facebook: "https://www.facebook.com/DGITAgency", linkedin: "https://np.linkedin.com/company/dg-it", email: "roshandaulyal@gmail.com", whatsapp: "9742846801" }
    },
    { name: "Aryan Pariyar", role: "Senior Web Developer", photo: "/uploads/aryan.jpg",
      bio: "Aryan Pariyar – Senior Web Developer Aryan Pariyar is a highly skilled Senior Web Developer at DG IT, specializing in creating fast, secure, and user-friendly websites. With expertise in modern web technologies, he ensures that every project meets high-performance standards and delivers a seamless user experience. His dedication to innovation and problem-solving makes him a key contributor to DG IT's success.",
      social: { facebook: "https://www.facebook.com/DGITAgency", linkedin: "https://np.linkedin.com/company/dg-it", email: "surajmahar200@gmail.com", whatsapp: "9742846801" }
    },
    { name: "Mahesh Bist", role: "Video Editor", photo: "/uploads/Mahesh.jpg",
      bio: "Mahesh Bist is a dedicated Video Editor at DG IT. With creative vision and precise editing skills, he brings ideas to life through impactful and engaging video content that aligns with the brand's goals.",
      social: { facebook: "https://www.facebook.com/DGITAgency", linkedin: "https://np.linkedin.com/company/dg-it", email: "nabinbohara2006@gmail.com", whatsapp: "+977 974-1818787" }
    }
  ];

  const values = [
    {
      title: "Honest & Transparent",
      description: "We believe in building trust through clear communication and delivering what we promise.",
      icon: "🤝",
    },
    {
      title: "Creative Teamwork",
      description: "Our diverse team collaborates to bring fresh perspectives and innovative solutions.",
      icon: "🚀",
    },
    {
      title: "Adaptive to New Tech",
      description: "We stay ahead by embracing new technologies and continuously improving our skills.",
      icon: "💡",
    },
    {
      title: "Client-Focused",
      description: "Your goals are our priority, and we tailor our solutions to meet your specific needs.",
      icon: "👥",
    },
  ];

  const TeamMemberCard = ({ member, index, shouldAnimate }: { member: TeamMember; index: number; shouldAnimate: boolean }) => {
    return (
      <motion.div 
        className="text-center"
        initial={shouldAnimate ? { opacity: 0, y: 20 } : false}
        animate={shouldAnimate ? { opacity: 1, y: 0 } : undefined}
        transition={{ duration: 0.6, delay: shouldAnimate ? index * 0.1 : 0 }}
        whileHover={{ y: -10 }}
        onClick={() => setSelectedMember(member)}
      >
        <div className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-xl cursor-pointer">
          <div className="h-48 overflow-hidden">
            <img 
              src={member.photo} 
              alt={member.name} 
              className="w-full h-full object-cover object-center transform transition-transform duration-500 hover:scale-110"
              loading="lazy"
            />
          </div>
          <div className="p-4">
            <h3 className="font-bold text-lg mb-1">{member.name}</h3>
            <p className="text-blue-600 text-sm">{member.role}</p>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <meta name="description" content="Learn more about DG IT, a creative digital agency providing web development, digital marketing, video production, animation, and design services in Nepal." />
        <meta name="keywords" content="about DG IT, digital agency Nepal, team, mission, vision, values" />
        <meta property="og:title" content="About Us - DG IT Pvt. Ltd." />
        <meta property="og:description" content="Learn more about our team, mission, vision and values." />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      
      <Navbar />
      <main className="flex-grow pt-24">
        <div className="container mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-800 mb-4">
              About Us
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We are a creative digital agency dedicated to providing innovative solutions and exceptional services to our clients.
            </p>
          </motion.div>

          {/* Our Mission */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-xl shadow-lg p-8"
            >
              <h2 className="text-3xl font-bold text-blue-700 mb-4">Our Mission</h2>
              <p className="text-gray-600 text-lg">
                To empower businesses with cutting-edge digital solutions that drive growth, enhance brand visibility, and create lasting impact.
              </p>
            </motion.div>
          </section>

          {/* Our Vision */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-xl shadow-lg p-8"
            >
              <h2 className="text-3xl font-bold text-blue-700 mb-4">Our Vision</h2>
              <p className="text-gray-600 text-lg">
                To be the leading digital agency in Nepal, recognized for our creativity, innovation, and commitment to client success.
              </p>
            </motion.div>
          </section>

          {/* Our Values */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <h2 className="text-3xl font-bold text-blue-700 mb-8">Our Core Values</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {values.map((value, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-xl shadow-lg p-6"
                  >
                    <div className="text-5xl mb-4">{value.icon}</div>
                    <h3 className="text-xl font-bold text-blue-700 mb-2">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </section>

          {/* Our Team */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <h2 className="text-3xl font-bold text-blue-700 mb-8">Meet Our Team</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
                We are a team of passionate and skilled professionals dedicated to delivering exceptional digital solutions.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {teamMembers.map((member, index) => (
                  <TeamMemberCard
                    key={`team-${member.name}`}
                    member={member}
                    index={index}
                    shouldAnimate={!hasAnimatedTeam.current}
                  />
                ))}
                {dbMembers.map((member, index) => (
                  <TeamMemberCard
                    key={`db-${member.name}`}
                    member={member}
                    index={teamMembers.length + index}
                    shouldAnimate={!hasAnimatedTeam.current}
                  />
                ))}
              </div>
            </motion.div>
          </section>

          {/* Careers */}
          <section id="careers" className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-xl shadow-lg p-8"
            >
              <h2 className="text-3xl font-bold text-blue-700 mb-4">Careers</h2>
              <p className="text-gray-600 text-lg mb-6">
                Join our team and be part of an exciting journey in the digital world. We're always looking for talented individuals who share our passion for innovation and excellence.
              </p>
              <a 
                href="/careers" 
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                View Open Positions
              </a>
            </motion.div>
          </section>

          {/* Team Member Modal */}
          <AnimatePresence>
            {selectedMember && (
              <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closeModal}
              >
                <motion.div
                  className="bg-white rounded-xl overflow-hidden max-w-4xl w-full max-h-[90vh] relative"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="absolute top-4 right-4 z-10 bg-white/80 backdrop-blur-sm p-2 rounded-full text-gray-700 hover:text-gray-900 hover:bg-white transition-colors"
                    onClick={closeModal}
                    aria-label="Close modal"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      <div className="md:w-1/3">
                        <img
                          src={selectedMember.photo}
                          alt={selectedMember.name}
                          className="w-full h-64 object-cover rounded-lg"
                        />
                      </div>
                      <div className="md:w-2/3">
                        <h3 className="text-2xl font-bold text-blue-700 mb-2">{selectedMember.name}</h3>
                        <p className="text-blue-600 font-medium mb-4">{selectedMember.role}</p>
                        <p className="text-gray-600 mb-6">{selectedMember.bio}</p>
                        <div className="flex gap-4">
                          {selectedMember.social.facebook && (
                            <a
                              href={selectedMember.social.facebook}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              <Facebook className="w-6 h-6" />
                            </a>
                          )}
                          {selectedMember.social.linkedin && (
                            <a
                              href={selectedMember.social.linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              <Linkedin className="w-6 h-6" />
                            </a>
                          )}
                          {selectedMember.social.email && (
                            <a
                              href={`mailto:${selectedMember.social.email}`}
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              <Mail className="w-6 h-6" />
                            </a>
                          )}
                          {selectedMember.social.whatsapp && (
                            <a
                              href={`https://wa.me/${selectedMember.social.whatsapp}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              <MessageCircle className="w-6 h-6" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
      <Newsletter />
      <Footer />
      <BackToTopButton />
      <FloatingSocialButtons />
    </div>
  );
};

export default AboutPage;
